// src/pages/PhysicaIntakeForm.jsx
import React, { useState, useRef, useEffect } from "react";
import { AlertCircle, ArrowRight, ArrowLeft, CheckCircle, Upload } from "lucide-react";
import questionsData from "./IntakeQuestions.json";
import { startIntake, saveAnswer as apiSaveAnswer, submitIntake } from "../api/intakeApi";

export default function PhysicaIntakeForm() {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [showRedFlag, setShowRedFlag] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [formData, setFormData] = useState({});
  const [intakeUid, setIntakeUid] = useState(null);
  const [hasStarted, setHasStarted] = useState(false);

  const questionCardRef = useRef(null);

  const questions = questionsData;
  const currentQ = questions[currentQuestion];
  const totalQuestions = questions.length;
  const progress = ((currentQuestion + 1) / totalQuestions) * 100;

  // Normalized sections to match QuickIntakeForm's naming
  const normalize = (s) =>
    s === "Red-Flag Screening" ? "Red-Flag Check"
      : s === "Preferences" ? "Your Preferences"
        : s === "Patient Basics" ? "About You"
          : s;

  const sections = Array.from(new Set(questions.map(q => normalize(q.section))));
  const currentSectionIndex = sections.indexOf(normalize(currentQ.section));

  // Auto-scroll the question card after the 3rd question shows up
  useEffect(() => {
    if (currentQuestion >= 3 && questionCardRef.current) {
      questionCardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [currentQuestion]);

  // Start the intake once we have name + phone + city
  // useEffect(() => {
  //   if (hasStarted || intakeUid) return;
  //   const name = (formData.fullName || "").trim();
  //   const phone = (formData.phone || "").trim();
  //   const city = (formData.city || "").trim();
  //   if (name && city && phone && phone.length === 10) {
  //     console.log("[FORM] Ready to start intake with:", { name, phone, city, form_type: "full" });
  //     startIntake({
  //       phone_number: `+91${phone}`,
  //       form_type: "full",
  //       name,
  //       city,
  //     })
  //       .then((res) => {
  //         console.log("[FORM] startIntake success:", res);
  //         setIntakeUid(res?.data?.uid);
  //         setHasStarted(true);
  //       })
  //       .catch((e) => console.error("startIntake error:", e));
  //   }
  // }, [formData.fullName, formData.phone, formData.city, hasStarted, intakeUid]);

  // Start only when City question is confirmed with NEXT
  // Start only when City question is confirmed with NEXT
  const startIfCityConfirmed = async () => {
    if (hasStarted || intakeUid) return intakeUid;

    const name = (formData.fullName || "").trim();
    const phone = (formData.phone || "").trim();
    const city = (formData.city || "").trim();

    if (!(name && city && phone && phone.length === 10)) {
      console.warn("[FORM] Not starting intake: missing name/phone(10)/city", { name, phone, city });
      return null;
    }

    const payload = {
      phone_number: `+91${phone}`,
      form_type: "minimum10",
      name,
      city,
    };

    console.log("[FORM] startIntake (on NEXT of City):", payload);
    try {
      const res = await startIntake(payload);
      console.log("[FORM] startIntake success:", res);

      // Be flexible about server shape:
      const uid =
        res?.data?.uid ??
        res?.data?.id ??
        res?.uid ??
        res?.id ?? null;

      if (!uid) {
        console.error("[FORM] startIntake returned no uid. Response shape mismatch.", res);
        return null;
      }

      setIntakeUid(uid);
      setHasStarted(true);
      return uid;
    } catch (e) {
      console.error("[FORM] startIntake error:", e);
      return null;
    }
  };



  // Red flag fields from JSON
  const redFlagFields = new Set(questions.filter(q => q.isRedFlag).map(q => q.field));
  const updateRedFlagState = (updated) => {
    const hasRed = Array.from(redFlagFields).some(f => updated[f] === "Yes");
    setShowRedFlag(hasRed);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      return next;
    });

    // Progressive save for single-value inputs (avoid for checkbox to reduce chatty writes)
    if (intakeUid && currentQ?.field === field && currentQ?.type !== "checkbox") {
      console.log("[FORM] saveAnswer (onChange):", { uid: intakeUid, question_key: field, answer: value });
      apiSaveAnswer({ uid: intakeUid, question_key: field, answer: value })
        .then((res) => console.log("[FORM] saveAnswer success:", res))
        .catch((e) => console.error("saveAnswer error:", e));
    }
  };

  const handleCheckboxChange = (field, value) => {
    setFormData(prev => {
      const current = prev[field] || [];
      return {
        ...prev,
        [field]: current.includes(value)
          ? current.filter(v => v !== value)
          : [...current, value]
      };
    });
  };

  const handleRedFlagChange = (field, value) => {
    const updated = { ...formData, [field]: value };
    setFormData(updated);
    updateRedFlagState(updated);


    if (intakeUid) {
      console.log("[FORM] saveAnswer (redflag):", { uid: intakeUid, question_key: field, answer: value });
      apiSaveAnswer({ uid: intakeUid, question_key: field, answer: value })
        .then((res) => console.log("[FORM] saveAnswer success:", res))
        .catch((e) => console.error("saveAnswer error:", e));
    }
  };

  const handleFileUpload = (e) =>
    setUploadedFiles(prev => [...prev, ...Array.from(e.target.files || [])]);

  const validateQuestion = () => {
    const { field, type, otherField } = currentQ || {};
    if (!field) return true;

    if (type === "upload") return true;

    if (type === "checkbox") {
      const selectedCount = (formData[field]?.length || 0);
      if (!selectedCount) return false;
      // If "Other" is selected, ensure other text is provided
      if (otherField && formData[field].includes("Other")) {
        return !!(formData[otherField]?.trim()?.length);
      }
      return true;
    }

    if (type === "slider" || type === "number") {
      return formData[field] !== undefined && formData[field] !== null && `${formData[field]}` !== "";
    }

    return !!(formData[field] && `${formData[field]}`.trim() !== "");
  };

  const goNext = async () => {
    if (!validateQuestion()) return;

    // If we are on the City question and we haven't started yet, START now
    if (currentQ?.field === "city" && !intakeUid) {
      const uid = await startIfCityConfirmed();
      if (!uid) {
        // If start failed, do not proceed (so the user can retry)
        return;
      }

      // Try to persist the City answer, but DO NOT block the navigation
      console.log("[FORM] saveAnswer (city after start):", {
        uid,
        question_key: "city",
        answer: formData.city,
      });
      apiSaveAnswer({ uid, question_key: "city", answer: formData.city })
        .then((res) => console.log("[FORM] saveAnswer (city) success:", res))
        .catch((e) => console.error("[FORM] saveAnswer (city) error:", e));

      // ✅ Advance immediately after /start succeeds
      if (currentQuestion < totalQuestions - 1) {
        setCurrentQuestion(prev => prev + 1);
      } else {
        handleSubmit();
      }
      return; // <- important: stop here
    }

    // For all other questions, save only if we already have a uid
    try {
      if (intakeUid && currentQ?.field) {
        console.log("[FORM] saveAnswer (onNext):", {
          uid: intakeUid,
          question_key: currentQ.field,
          answer: formData[currentQ.field],
        });
        await apiSaveAnswer({
          uid: intakeUid,
          question_key: currentQ.field,
          answer: formData[currentQ.field],
        });
        console.log("[FORM] saveAnswer (onNext) success");
      }
    } catch (e) {
      console.error("[FORM] saveAnswer (onNext) error:", e);
      // optional: return;  // if you want to block on save failure for later steps
    }

    // Advance or submit
    if (currentQuestion < totalQuestions - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      handleSubmit();
    }
  };



  const handleSubmit = async () => {
    try {
      // Save uploaded filenames as a final answer (optional)
      if (intakeUid && uploadedFiles.length) {
        console.log("[FORM] saveAnswer (uploads):", {
          uid: intakeUid, question_key: "uploadedFiles",
          answer: uploadedFiles.map(f => f.name)
        });
        await apiSaveAnswer({
          uid: intakeUid,
          question_key: "uploadedFiles",
          answer: uploadedFiles.map(f => f.name),
        });
        console.log("[FORM] saveAnswer (uploads) success");
      }
      if (intakeUid) {
        console.log("[FORM] submitIntake:", { uid: intakeUid });
        await submitIntake({ uid: intakeUid });
        console.log("[FORM] submitIntake success");
      }
    } catch (err) {
      console.error("submitIntake error:", err);
    }
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.logoContainer}>
            <div style={styles.textContainer}>
              <h1 style={styles.logoText}>Physica</h1>
              <p style={styles.subtitle}>This intake form is co-created with experts</p>
            </div>
          </div>
        </div>

        <div style={styles.thankYouCard}>
          <CheckCircle size={64} color="#FE8235" style={{ marginBottom: 24 }} />
          <h1 style={styles.thankYouTitle}>Thank You for Registering!</h1>
          <p style={styles.thankYouText}>
            Thank you for registering with Physica. A Care Manager will contact you immediately.
          </p>
          <div style={styles.privacyNote}>
            <p>
              *Your data is safe with us. We follow strict security measures to protect your privacy
              and never share your information without consent. <a href="#" style={styles.link}>Learn more</a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header (same as QuickIntakeForm) */}
      <div style={styles.header}>
        <div style={styles.logoContainer}>
          <div style={styles.textContainer}>
            <h1 style={styles.logoText}>Physica</h1>
            <p style={styles.subtitle}>This intake form is co-created with experts</p>
          </div>
        </div>
      </div>

      <div style={styles.mainContent}>
        {/* Back arrow (left) + centered section name (orange) */}
        <div style={styles.sectionHeader}>
          {currentQuestion > 0 && (
            <ArrowLeft
              size={24}
              color="#4A4A4A"
              style={styles.backArrow}
              onClick={() => setCurrentQuestion(prev => Math.max(prev - 1, 0))}
            />
          )}
          <div style={styles.sectionCenter}>
            <span style={styles.sectionName}>{sections[currentSectionIndex]}</span>
          </div>
        </div>

        {/* Progress */}
        <div style={styles.progressContainer}>
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: `${progress}%` }} />
          </div>
          <div style={styles.progressText}>{Math.round(progress)}%</div>
        </div>

        {/* Red Flag Alert */}
        {showRedFlag && (
          <div style={styles.redFlagAlert}>
            <AlertCircle size={20} />
            <div>
              <strong>⚠️ Medical Alert</strong>
              <p>
                Based on your responses, immediate medical attention may be required.
                Our Care Manager will prioritize your case.
              </p>
            </div>
          </div>
        )}

        {/* Question Card */}
        <div style={styles.questionCard} ref={questionCardRef}>
          <h2 style={styles.questionTitle}>{currentQ.question}</h2>

          <div style={styles.answerArea}>
            {/* TEXT */}
            {currentQ.type === "text" && (
              <input
                type="text"
                value={formData[currentQ.field] || ""}
                onChange={(e) => handleInputChange(currentQ.field, e.target.value)}
                style={styles.textInput}
                placeholder={currentQ.placeholder || ""}
              />
            )}

            {/* NUMBER */}
            {currentQ.type === "number" && (
              <input
                type="number"
                value={formData[currentQ.field] || ""}
                onChange={(e) => handleInputChange(currentQ.field, e.target.value)}
                style={styles.textInput}
                placeholder={currentQ.placeholder || "Enter number"}
              />
            )}

            {/* TEL (+91 prefix) */}
            {currentQ.type === "tel" && (
              <div style={styles.phoneWrapper}>
                <span style={styles.phonePrefix}>+91</span>
                <input
                  type="tel"
                  value={formData[currentQ.field] || ""}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                    handleInputChange(currentQ.field, digits);
                  }}
                  style={styles.phoneInput}
                  placeholder="98765 43210"
                />
              </div>
            )}

            {/* SELECT */}
            {currentQ.type === "select" && (
              <select
                value={formData[currentQ.field] || ""}
                onChange={(e) => handleInputChange(currentQ.field, e.target.value)}
                style={styles.select}
              >
                <option value="">{currentQ.placeholder || "Select option"}</option>
                {currentQ.options?.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            )}

            {/* RADIO (auto-advance) */}
            {currentQ.type === "radio" && (
              <div style={styles.radioGroup}>
                {currentQ.options?.map(option => {
                  const isSelected = formData[currentQ.field] === option;
                  return (
                    <label
                      key={option}
                      style={{
                        ...styles.radioLabel,
                        background: isSelected ? "#FE8235" : "#FFFFFF",
                        color: isSelected ? "#FFFFFF" : "#4A4A4A",
                        border: isSelected ? "2px solid #FE8235" : "2px solid #E0E0E0",
                      }}
                    >
                      <input
                        type="radio"
                        name={currentQ.field}
                        value={option}
                        checked={isSelected}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (currentQ.isRedFlag) handleRedFlagChange(currentQ.field, v);
                          else handleInputChange(currentQ.field, v);

                          // Auto-advance after a brief delay
                          setTimeout(() => {
                            if (currentQuestion < totalQuestions - 1) setCurrentQuestion(prev => prev + 1);
                            else handleSubmit();
                          }, 300);
                        }}
                        style={styles.radioInput}
                      />
                      {option}
                    </label>
                  );
                })}
              </div>
            )}

            {/* CHECKBOX (+ optional "Other" field) */}
            {currentQ.type === "checkbox" && (
              <>
                <div style={styles.checkboxGroup}>
                  {currentQ.options?.map(option => {
                    const selected = formData[currentQ.field]?.includes(option);
                    return (
                      <label
                        key={option}
                        style={{
                          ...styles.checkboxLabel,
                          background: selected ? "#FE8235" : "#FFFFFF",
                          color: selected ? "#FFFFFF" : "#4A4A4A",
                          border: selected ? "2px solid #FE8235" : "2px solid #E0E0E0",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={!!selected}
                          onChange={() => handleCheckboxChange(currentQ.field, option)}
                          style={styles.checkboxInput}
                        />
                        {option}
                      </label>
                    );
                  })}
                </div>
                {currentQ.otherField && (
                  <input
                    type="text"
                    value={formData[currentQ.otherField] || ""}
                    onChange={(e) => handleInputChange(currentQ.otherField, e.target.value)}
                    style={styles.textInput}
                    placeholder="Other (please specify)"
                  />
                )}
              </>
            )}

            {/* SLIDER */}
            {currentQ.type === "slider" && (
              <div style={styles.sliderContainer}>
                <div style={styles.sliderValue}>{formData[currentQ.field] ?? currentQ.min ?? 0}</div>
                <input
                  type="range"
                  min={currentQ.min ?? 0}
                  max={currentQ.max ?? 10}
                  value={formData[currentQ.field] ?? currentQ.min ?? 0}
                  onChange={(e) => handleInputChange(currentQ.field, parseInt(e.target.value, 10))}
                  style={styles.slider}
                />
                <div style={styles.sliderLabels}>
                  <span>No Pain</span>
                  <span>Worst Pain</span>
                </div>
              </div>
            )}

            {/* TEXTAREA */}
            {currentQ.type === "textarea" && (
              <textarea
                value={formData[currentQ.field] || ""}
                onChange={(e) => handleInputChange(currentQ.field, e.target.value)}
                style={styles.textarea}
                placeholder={currentQ.placeholder || ""}
                rows={4}
              />
            )}

            {/* UPLOAD */}
            {currentQ.type === "upload" && (
              <div style={styles.uploadContainer}>
                <div style={styles.uploadArea}>
                  <Upload size={40} color="#FE8235" />
                  <p style={styles.uploadText}>Click to upload or drag and drop</p>
                  <p style={styles.uploadSubtext}>PDF, JPG, PNG (max 10MB)</p>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileUpload}
                    style={styles.fileInput}
                  />
                </div>
                {uploadedFiles.length > 0 && (
                  <div style={styles.fileList}>
                    {uploadedFiles.map((file, idx) => (
                      <div key={idx} style={styles.fileItem}>
                        <CheckCircle size={18} color="#FE8235" />
                        <span>{file.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* CONDITIONAL DETAILS FIELD (when radio = Yes) */}
            {currentQ.detailsField && formData[currentQ.field] === "Yes" && (
              <textarea
                value={formData[currentQ.detailsField] || ""}
                onChange={(e) => handleInputChange(currentQ.detailsField, e.target.value)}
                style={styles.textarea}
                placeholder="Please provide details"
                rows={3}
              />
            )}
          </div>

          <button
            onClick={goNext}
            style={{ ...styles.nextButton, opacity: validateQuestion() ? 1 : 0.5 }}
            disabled={!validateQuestion()}
          >
            {currentQuestion === totalQuestions - 1 ? "SUBMIT" : "NEXT"} <ArrowRight size={20} />
          </button>

          <div style={styles.privacyNote}>
            <p>
              *Your data is safe with us. We follow strict security measures to protect your privacy
              and never share your information without consent. <a href="#" style={styles.link}>Learn more</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ===== Styles (mirrors QuickIntakeForm) ===== */
const styles = {
  container: {
    minHeight: '100vh',
    background: '#F5F5F5',
    fontFamily: 'Inter, system-ui, sans-serif',
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    background: '#FE8235',
    padding: '20px 25px',
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
  },
  logoImage: {
    height: '48px',
    width: '48px',
    objectFit: 'contain',
  },
  backContainer: {
    textAlign: 'left',
    marginBottom: '15px'
  },

  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    lineHeight: '1.2',
  },
  logoText: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#FFFFFF',
    margin: 0,
    letterSpacing: '0.5px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#E0E0E0',
    margin: 0,
    marginTop: '4px',
  },
  mainContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: '15px',
    maxWidth: '960px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box'
  },
  exitContainer: {
    textAlign: 'right',
    marginBottom: '15px'
  },
  sectionTabs: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: '10px',
    marginBottom: '20px'
  },
  tab: {
    padding: '14px 20px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '600',
    transition: 'all 0.3s',
    textAlign: 'center',
    whiteSpace: 'nowrap'
  },
  progressContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px'
  },
  progressBar: {
    flex: 1,
    height: '8px',
    background: '#E0E0E0',
    borderRadius: '20px',
    overflow: 'hidden'
  },
  progressFill: {
    height: '100%',
    background: '#FE8235',
    transition: 'width 0.3s ease',
    borderRadius: '20px'
  },
  progressText: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#5A5A5A',
    minWidth: '45px'
  },
  redFlagAlert: {
    marginBottom: '15px',
    background: '#FEE',
    border: '2px solid #C33',
    borderRadius: '8px',
    padding: '12px',
    display: 'flex',
    gap: '10px',
    alignItems: 'start',
    color: '#C33',
    fontSize: '13px'
  },
  questionCard: {
    flex: 1,
    background: '#FFFFFF',
    borderRadius: '0',
    padding: '40px 30px 30px',
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0
  },
  questionTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#3A3A3A',
    marginBottom: '40px',
    textAlign: 'left',
    lineHeight: '1.4'
  },
  answerArea: {
    // marginBottom: 'auto',
    flex: 0,
    overflowY: 'auto',
    minHeight: 0,
    paddingBottom: '8px'
  },
  phoneWrapper: {
    display: 'flex',
    alignItems: 'center',
    borderBottom: '2px solid #FE8235',
    paddingBottom: '8px',
    overflow: 'hidden',
    width: '100%',           // ✅ ensures it fits parent
    boxSizing: 'border-box',
  },
  phonePrefix: {
    color: '#3A3A3A',
    fontWeight: '600',
    marginRight: '8px',
    fontSize: '20px',
  },
  phoneInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: '20px',
    color: '#3A3A3A',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    marginBottom: '15px',
    height: '30px',
  },

  backArrow: {
    position: 'absolute',
    left: 0,
    cursor: 'pointer',
  },

  sectionCenter: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
  },

  sectionName: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#FE8235',
    textAlign: 'center',
  },


  textInput: {
    width: '100%',
    padding: '8px 0',
    fontSize: '20px',
    border: 'none',
    borderBottom: '2px solid #FE8235',
    outline: 'none',
    background: 'transparent',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
    color: '#3A3A3A'
  },
  select: {
    width: '100%',
    padding: '8px 0',
    fontSize: '20px',
    border: 'none',
    borderBottom: '2px solid #FE8235',
    outline: 'none',
    background: 'transparent',
    cursor: 'pointer',
    boxSizing: 'border-box',
    color: '#3A3A3A'
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '16px',
    cursor: 'pointer',
    padding: '16px 20px',
    borderRadius: '8px',
    transition: 'all 0.2s'
  },
  radioInput: {
    width: '20px',
    height: '20px',
    cursor: 'pointer',
    accentColor: '#FE8235',
    flexShrink: 0
  },
  nextButton: {
    width: '100%',
    padding: '10px',
    fontSize: '16px',
    fontWeight: '700',
    color: '#FFFFFF',
    background: '#FE8235',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    marginTop: '20px',
    marginBottom: '20px',
    flexShrink: 0,
    letterSpacing: '1px'
  },
  privacyNote: {
    textAlign: 'center',
    fontSize: '11px',
    color: '#999',
    lineHeight: '1.6',
    flexShrink: 0
  },
  link: {
    color: '#999',
    textDecoration: 'underline'
  },
  thankYouCard: {
    maxWidth: '600px',
    margin: '80px auto',
    background: '#FFFFFF',
    borderRadius: '12px',
    padding: '50px 30px',
    textAlign: 'center',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
  },
  thankYouTitle: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#3A3A3A',
    margin: '0 0 16px 0'
  },
  thankYouText: {
    fontSize: '16px',
    color: '#666',
    lineHeight: '1.6',
    marginBottom: '40px'
  }
};
